/**
 * SigningWizard - Parallel multi-step transaction signing for trades
 *
 * All Xaman payloads are live simultaneously from the start.
 * Users can sign in any order. The displayed QR code follows
 * the selected step but all monitors run in parallel.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FeatherCheck, FeatherClock, FeatherX, FeatherLoader } from '@subframe/core';
import { confirmTradeOffer, confirmTradeEscrow } from '../../api/trades';
import { getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import SigningStepMonitor from './SigningStepMonitor';

export interface SigningStep {
  type: 'nft_offer' | 'escrow';
  label: string;
  nftTokenId?: string;
  payload: SigningPayload;
  status: 'pending' | 'signing' | 'done' | 'failed';
}

interface SigningWizardProps {
  tradeId: string;
  steps: SigningStep[];
  onComplete: () => void;
  onClose: () => void;
  timeoutMinutes?: number;
}

export default function SigningWizard({
  tradeId,
  steps: initialSteps,
  onComplete,
  onClose,
  timeoutMinutes = 30,
}: SigningWizardProps) {
  // All steps start as 'signing' immediately (parallel)
  const [steps, setSteps] = useState<SigningStep[]>(() =>
    initialSteps.map((s) => ({ ...s, status: 'signing' as const }))
  );
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(timeoutMinutes * 60);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const selectedStep = steps[selectedStepIndex];
  const completedCount = steps.filter((s) => s.status === 'done').length;
  const progress = (completedCount / steps.length) * 100;

  // Client-signing wallets (joey-wc) sign over a single WC session — one request at a
  // time, so steps must run SEQUENTIALLY. Xaman payloads are all live at once (parallel).
  const isClientSigning = steps.length > 0 && getWalletProvider(steps[0].payload.provider).capabilities.clientSigning;

  // For client-signing, auto-focus the first un-done step so its (single) monitor mounts.
  // CRITICAL: bail once an error is set. Otherwise, after the user REJECTS a step, this
  // would select the next 'signing' step, mounting its monitor — which for joey-wc auto-
  // fires an irreversible WalletConnect broadcast the user never asked for.
  useEffect(() => {
    if (!isClientSigning || error) return;
    const nextIdx = steps.findIndex((s) => s.status === 'signing');
    if (nextIdx >= 0 && nextIdx !== selectedStepIndex) setSelectedStepIndex(nextIdx);
  }, [isClientSigning, steps, selectedStepIndex, error]);

  /**
   * On a failure, mark the failing step failed. For client-signing (sequential WC), also
   * terminalize every other still-'signing' step so no further monitor can mount and auto-
   * broadcast — one failure stops the whole sequential run; the user retries the trade.
   * Xaman (parallel) leaves its other independent steps alone.
   */
  const failStep = useCallback(
    (prev: SigningStep[], index: number): SigningStep[] =>
      prev.map((s, i) => {
        if (i === index) return { ...s, status: 'failed' as const };
        if (isClientSigning && s.status === 'signing') return { ...s, status: 'failed' as const };
        return s;
      }),
    [isClientSigning]
  );

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setTimedOut(true);
          setError('Signing session has expired. Please try again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-advance selected step to next pending when current completes
  const advanceToNextPending = useCallback((currentSteps: SigningStep[], justCompletedIndex: number) => {
    setSelectedStepIndex((prev) => {
      // Only auto-advance if we're viewing the step that just completed
      if (prev !== justCompletedIndex) return prev;
      const nextPending = currentSteps.findIndex(
        (s, i) => i > justCompletedIndex && s.status === 'signing'
      );
      if (nextPending !== -1) return nextPending;
      // Wrap around: find first still-signing step
      const firstPending = currentSteps.findIndex((s) => s.status === 'signing');
      if (firstPending !== -1) return firstPending;
      // All done - stay on current
      return prev;
    });
  }, []);

  // Check if all steps are done and call onComplete
  const checkAllDone = useCallback((currentSteps: SigningStep[]) => {
    if (currentSteps.every((s) => s.status === 'done')) {
      onCompleteRef.current();
    }
  }, []);

  // Handlers for each step's SigningStepMonitor
  const handleStepSigned = useCallback(async (index: number, txHash: string) => {
    const step = stepsRef.current[index];
    if (!step || step.status === 'done') return;

    try {
      // Confirm with backend
      if (step.type === 'nft_offer' && step.nftTokenId) {
        await confirmTradeOffer(tradeId, {
          nftTokenId: step.nftTokenId,
          offerTxHash: txHash,
        });
      } else if (step.type === 'escrow') {
        await confirmTradeEscrow(tradeId, {
          escrowTxHash: txHash,
        });
      }

      // Mark step as done
      setSteps((prev) => {
        const updated = prev.map((s, i) =>
          i === index ? { ...s, status: 'done' as const } : s
        );
        advanceToNextPending(updated, index);
        // Defer completion check to next tick to ensure state updates
        setTimeout(() => checkAllDone(updated), 0);
        return updated;
      });
    } catch (err: any) {
      console.error(`Failed to confirm step ${index}:`, err);
      setError(err.message || 'Failed to confirm transaction. Please try again.');
      setSteps((prev) =>
        prev.map((s, i) => (i === index ? { ...s, status: 'failed' as const } : s))
      );
    }
  }, [tradeId, advanceToNextPending, checkAllDone]);

  const handleStepRejected = useCallback((index: number) => {
    setSteps((prev) => failStep(prev, index));
    setError(isClientSigning ? 'Transaction was rejected' : 'Transaction was rejected in Xaman');
  }, [failStep, isClientSigning]);

  const handleStepExpired = useCallback((index: number, message?: string) => {
    setSteps((prev) => failStep(prev, index));
    // A client-signing step may report a soft "broadcast but unconfirmed" outcome through
    // this channel — show that message rather than implying the user should re-sign.
    setError(message || 'Signing request expired. Please try again.');
  }, [failStep]);

  const handleStepError = useCallback((index: number, msg: string) => {
    setSteps((prev) => failStep(prev, index));
    setError(msg);
  }, [failStep]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-default-background rounded-md border border-solid border-neutral-border shadow-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <h2 className="text-body-bold font-body-bold text-default-font">Sign Your Side of the Trade</h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-md">
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 pt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-default-font font-medium">
              {completedCount} of {steps.length} signed
            </span>
            <span className="text-subtext-color flex items-center gap-1">
              <FeatherClock className="h-3.5 w-3.5" />
              {formatTime(timeRemaining)} remaining
            </span>
          </div>
          <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps list - clickable */}
        <div className="px-4 py-4 space-y-2">
          {steps.map((step, index) => {
            const isSelected = index === selectedStepIndex;
            const isClickable = step.status === 'signing';

            return (
              <button
                key={index}
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && setSelectedStepIndex(index)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                  isSelected
                    ? 'bg-brand-50 border border-brand-200'
                    : isClickable
                      ? 'bg-neutral-50 hover:bg-neutral-100 cursor-pointer'
                      : 'bg-neutral-50'
                }`}
              >
                <div className="flex-shrink-0">
                  {step.status === 'done' ? (
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <FeatherCheck className="h-3.5 w-3.5 text-green-600" />
                    </div>
                  ) : step.status === 'signing' ? (
                    <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center animate-pulse">
                      <FeatherLoader className="h-3.5 w-3.5 text-brand-primary animate-spin" />
                    </div>
                  ) : step.status === 'failed' ? (
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                      <FeatherX className="h-3.5 w-3.5 text-red-600" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center">
                      <span className="text-xs text-subtext-color">{index + 1}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-default-font">{step.label}</div>
                  <div className="text-xs text-subtext-color">
                    {step.status === 'done'
                      ? 'Signed'
                      : step.status === 'signing'
                        ? isSelected ? 'Scan QR or tap Open in Xaman' : 'Waiting for signature...'
                        : step.status === 'failed'
                          ? 'Failed'
                          : 'Pending'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* QR Code / Deep Link for selected step — Xaman only (client-signing has no QR) */}
        {selectedStep && selectedStep.status === 'signing' && selectedStep.payload.qrUrl && (
          <div className="px-4 pb-4">
            <div className="flex flex-col items-center gap-3 p-4 bg-neutral-50 rounded-lg">
              <img
                src={selectedStep.payload.qrUrl}
                alt="Scan with Xaman"
                className="w-48 h-48"
              />
              {selectedStep.payload.deepLink && (
                <a
                  href={selectedStep.payload.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-md hover:bg-brand-primary/90 transition-colors"
                >
                  Open in Xaman
                </a>
              )}
              <p className="text-xs text-subtext-color text-center">
                Scan the QR code or tap the button to sign in Xaman
              </p>
            </div>
          </div>
        )}

        {/* Client-signing prompt (joey-wc) — no QR; approve each tx on the phone */}
        {isClientSigning && selectedStep && selectedStep.status === 'signing' && (
          <div className="px-4 pb-4">
            <div className="flex flex-col items-center gap-2 p-4 bg-neutral-50 rounded-lg">
              <FeatherLoader className="w-6 h-6 text-brand-600 animate-spin" />
              <p className="text-sm text-default-font text-center">
                Approve "{selectedStep.label}" in your wallet ({completedCount + 1} of {steps.length})
              </p>
            </div>
          </div>
        )}

        {/* Monitors render nothing. Xaman: all signing steps run in parallel.
            Client-signing: only the selected step's monitor mounts (sequential). */}
        {!timedOut && steps.map((step, index) => {
          const active =
            step.status === 'signing' &&
            (!isClientSigning || (index === selectedStepIndex && !error));
          return active ? (
            <SigningStepMonitor
              key={step.payload.id}
              payload={step.payload}
              onSigned={(txHash) => handleStepSigned(index, txHash)}
              onRejected={() => handleStepRejected(index)}
              onExpired={(msg) => handleStepExpired(index, msg)}
              onError={(msg) => handleStepError(index, msg)}
            />
          ) : null;
        })}

        {/* Error */}
        {error && (
          <div className="px-4 pb-4">
            <div className="text-sm text-error-700 bg-error-50 border border-error-200 rounded-md px-3 py-2">
              {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-solid border-neutral-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-default-font bg-default-background border border-neutral-border rounded-md hover:bg-neutral-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
